import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, ArrowRight, CheckCircle, XCircle, Clock } from 'lucide-react';

interface Template {
  _id: string;
  title: string;
  description: string;
  price: number;
  imageUrls: string[];
  status: 'pending' | 'approved' | 'rejected';
}

export default function TemplatesTable() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [templates] = useState<Template[]>([
    {
      _id: '1',
      title: 'E-commerce Template',
      description: 'Modern online store template with product management',
      price: 149.99,
      imageUrls: ['https://via.placeholder.com/80'],
      status: 'approved'
    },
    {
      _id: '2',
      title: 'Portfolio Template',
      description: 'Professional portfolio with project showcase',
      price: 89.99,
      imageUrls: [],
      status: 'pending'
    },
  ]);

  const filteredTemplates = templates.filter(template =>
    template.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'approved': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'rejected': 'bg-red-100 text-red-800',
      'default': 'bg-navy-100 text-navy-800'
    };
    return colors[status] || colors.default;
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      'approved': <CheckCircle size={16} className="text-green-500" />,
      'pending': <Clock size={16} className="text-yellow-500" />,
      'rejected': <XCircle size={16} className="text-red-500" />
    };
    return icons[status as keyof typeof icons] || null;
  };

  const handleTemplateClick = (id: string) => {
    navigate(`/templates/details/${id}`);
  };

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="flex items-center space-x-2">
          <div className="p-2 bg-navy-900 rounded-lg">
            <Grid size={24} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-center text-navy-900">Templates</h1>
        </div>
        
        <div className="relative w-full sm:w-96">
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border-2 border-navy-100 rounded-xl focus:ring-4 focus:ring-navy-100 focus:border-navy-500 transition-all"
          />
          <Search size={20} className="absolute left-4 top-3.5 text-navy-400" />
        </div>
      </div>

      <div className="border-2 border-navy-100 rounded-xl overflow-hidden shadow-lg">
        <div className="hidden sm:grid grid-cols-12 bg-navy-50 p-4 text-sm font-medium text-navy-900">
          <div className="col-span-8">Template</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Price</div>
        </div>

        {filteredTemplates.map((template) => (
          <div
            key={template._id}
            onClick={() => handleTemplateClick(template._id)}
            className="group grid sm:grid-cols-12 gap-4 p-4 border-b border-navy-100 hover:bg-white cursor-pointer transition-all duration-300 hover:shadow-[0_8px_30px_rgb(0,31,63,0.05)]"
          >
            {/* Template Preview & Info */}
            <div className="col-span-8 flex items-center space-x-4">
              <div className="w-20 h-20 bg-navy-100 rounded-lg overflow-hidden flex items-center justify-center">
                {template.imageUrls?.length > 0 ? (
                  <img 
                    src={template.imageUrls[0]} 
                    alt={template.title} 
                    className="object-cover w-full h-full" 
                  />
                ) : (
                  <Grid size={24} className="text-navy-400" />
                )}
              </div>
              <div>
                <div className="font-medium text-navy-900 group-hover:text-navy-700 transition-colors">
                  {template.title}
                  <ArrowRight size={16} className="ml-2 inline-block opacity-0 group-hover:opacity-100 transition-all" />
                </div>
                <div className="text-sm text-navy-600 line-clamp-1">{template.description}</div>
              </div>
            </div>
            
            {/* Status */}
            <div className="col-span-2 flex items-center space-x-2">
              {getStatusIcon(template.status)}
              <span className={`text-sm font-medium ${getStatusColor(template.status)} px-3 py-1 rounded-full`}>
                {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
              </span>
            </div>
            
            {/* Price */}
            <div className="col-span-2 text-right">
              <div className="space-y-1">
                <span className="block text-lg font-bold text-navy-900">
                  ${template.price.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}